/**
 * The scheduler's UI model: the slice, live capacity recomputed from sessions with the same pure
 * `schedule` the hub runs, tones and labels. Pure, so it is unit tested.
 */
import { ADMISSION_STATE_LABEL, MACHINE_LOAD_TTL_MS, WAIT_REASON_LABEL, describeRule, effectiveLabels, nodeLimit, roomFor, runningWork, schedule } from '@orgus/protocol';
import type {
  Admission,
  AdmissionState,
  CheckoutRef,
  Id,
  LoadSignal,
  Machine,
  MachineLoadState,
  RoomVerdict,
  MissionsState,
  NodeCapacity,
  NodeSettings,
  QueueEntry,
  QueueState,
  SchedulerState,
  Session,
  WaitReason,
} from '@orgus/protocol';
import type { StatusTone } from '@orgus/ui';

export const SCHEDULER_EXT = 'scheduler';

const isObj = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** state.ext.scheduler, when the hub has the module. */
export function schedulerSlice(ext: Record<string, unknown>): SchedulerState | null {
  const s = ext[SCHEDULER_EXT];
  return isObj(s) && Array.isArray(s.queue) && Array.isArray(s.capacity) ? (s as unknown as SchedulerState) : null;
}

/** Who owns each session: the owner of the mission that started it. */
export function ownerIndex(missions: MissionsState | null | undefined): Map<Id, string> {
  const out = new Map<Id, string>();
  for (const m of missions?.missions ?? []) {
    for (const id of m.sessionIds) out.set(id, m.owner);
    for (const l of m.launches) if (l.sessionId) out.set(l.sessionId, m.owner);
  }
  return out;
}

export interface LiveSources {
  machines: Record<Id, Machine>;
  sessions: Record<Id, Session>;
  checkouts: Record<Id, CheckoutRef>;
  /** Fresh load reports (`loadsFrom`): room is heavy work and memory where a machine reports it (ADR 0063). */
  loads?: Record<Id, LoadSignal>;
}

/**
 * Load reports younger than MACHINE_LOAD_TTL_MS from state.ext['machine-load'], by machine id.
 * The hub stops sending a machine that went quiet, so the console drops it by age too; demo data
 * (?mock=1) never ages.
 */
export function loadsFrom(ext: Record<string, unknown>, now: number, mock = false): Record<Id, LoadSignal> {
  const s = ext['machine-load'] as MachineLoadState | undefined;
  const out: Record<Id, LoadSignal> = {};
  for (const e of Array.isArray(s?.machines) ? s.machines : []) if (mock || now - e.at <= MACHINE_LOAD_TTL_MS) out[e.machineId] = e;
  return out;
}

/**
 * Capacity as of now: the hub's settings and queue with this page's live sessions and machines,
 * through the same `schedule` the hub runs, so bars move with every session patch.
 */
export function liveCapacity(state: SchedulerState, src: LiveSources, missions: MissionsState | null | undefined, now: number): NodeCapacity[] {
  const owners = ownerIndex(missions);
  const machines = Object.values(src.machines);
  if (!machines.length) return [];
  return schedule({
    now,
    machines,
    nodes: state.nodes,
    settings: state.settings,
    running: runningWork(Object.values(src.sessions), state.queue, (id) => owners.get(id) ?? null, now),
    queue: [],
    checkouts: Object.values(src.checkouts),
    ...(src.loads ? { loads: src.loads } : {}),
  }).capacity;
}

/**
 * Whether one more session has room, per machine, best first (the New session dialog): the same
 * `roomFor` the hub's start and the assistant use.
 */
export function liveRoom(state: SchedulerState, src: LiveSources, missions: MissionsState | null | undefined, now: number, projectId: Id | null): RoomVerdict[] {
  const owners = ownerIndex(missions);
  const machines = Object.values(src.machines);
  if (!machines.length) return [];
  return roomFor(
    {
      now,
      machines,
      nodes: state.nodes,
      settings: state.settings,
      running: runningWork(Object.values(src.sessions), state.queue, (id) => owners.get(id) ?? null, now),
      queue: [],
      checkouts: Object.values(src.checkouts),
      ...(src.loads ? { loads: src.loads } : {}),
    },
    projectId,
  );
}

export type Preview = { kind: 'start'; machineId: Id; machineName: string } | { kind: 'wait'; reason: WaitReason; message: string };

/**
 * What would happen if this were queued now: the machine it would start on, or why it would wait.
 * The same `schedule` as the hub, with the queue ahead of it.
 */
export function previewPlacement(
  state: SchedulerState,
  src: LiveSources,
  missions: MissionsState | null | undefined,
  now: number,
  draft: { projectId: Id; labels: string[]; machineId: Id | null; person: string; priority?: number },
): Preview {
  const owners = ownerIndex(missions);
  const probe: QueueEntry = {
    id: '~preview',
    missionId: '~preview',
    projectId: draft.projectId,
    title: '',
    person: draft.person,
    by: draft.person,
    needs: { labels: draft.labels, runtime: 'claude-code', minFreeMemGb: null },
    machineId: draft.machineId,
    start: { mode: 'headless', model: null, permissionMode: null, newWorktree: null, note: null },
    priority: draft.priority ?? 2,
    state: 'queued',
    enqueuedAt: now,
    updatedAt: now,
    attempts: 0,
    reason: null,
    message: null,
    placedOn: null,
    checkoutId: null,
    startedAt: null,
    launchId: null,
    sessionId: null,
  };
  const r = schedule({
    now,
    machines: Object.values(src.machines),
    nodes: state.nodes,
    settings: state.settings,
    running: runningWork(Object.values(src.sessions), state.queue, (id) => owners.get(id) ?? null, now),
    queue: [...state.queue, probe],
    checkouts: Object.values(src.checkouts),
    ...(src.loads ? { loads: src.loads } : {}),
  });
  const p = r.placements.find((x) => x.entryId === probe.id);
  if (p) return { kind: 'start', machineId: p.machineId, machineName: src.machines[p.machineId]?.name ?? 'a machine' };
  const w = r.waiting.find((x) => x.entryId === probe.id)!;
  return { kind: 'wait', reason: w.reason, message: w.message };
}

/** Labels and the limit for one machine, as the scheduler sees it (for the Machines cards). */
export function nodeView(m: Machine, state: SchedulerState | null): { labels: string[]; agentLabels: string[]; set: boolean; limit: number; drain: NodeSettings['drain'] } {
  const node = state?.nodes[m.id];
  return { labels: effectiveLabels(m, node), agentLabels: effectiveLabels(m), set: node?.labels != null, limit: nodeLimit(m, node), drain: node?.drain ?? null };
}

// ---------------------------------------------------------------------------
// Tones and labels
// ---------------------------------------------------------------------------

export function queueTone(s: QueueState): StatusTone {
  switch (s) {
    case 'queued':
      return 'queued';
    case 'starting':
      return 'working';
    case 'started':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'cancelled':
      return 'ended';
  }
}

/** Waiting on a person (a limit, a pin) reads as waiting; waiting on machines reads as queued. */
export function waitTone(r: WaitReason | null): StatusTone {
  if (!r) return 'queued';
  if (r === 'no-checkout' || r === 'runtime' || r === 'labels' || r === 'no-machine') return 'waiting';
  if (r === 'pinned-offline' || r === 'offline') return 'offline';
  return 'queued';
}

export function waitLabel(e: Pick<QueueEntry, 'state' | 'reason'>): string {
  if (e.state === 'queued' && e.reason) return WAIT_REASON_LABEL[e.reason];
  return e.state === 'queued' ? 'Queued' : e.state === 'starting' ? 'Starting' : e.state === 'started' ? 'Started' : e.state === 'failed' ? 'Failed' : 'Cancelled';
}

/** Fill tone for a capacity bar. */
export function capacityTone(c: Pick<NodeCapacity, 'used' | 'limit' | 'online' | 'accepting'>): StatusTone | 'accent' {
  if (!c.online) return 'offline';
  if (!c.accepting) return 'idle';
  if (c.limit > 0 && c.used >= c.limit) return 'waiting';
  return 'working';
}

/** The tone of an admission state: room runs, nearly full is quiet, full waits on room. */
export function admissionTone(s: AdmissionState): StatusTone {
  return s === 'room' ? 'working' : s === 'tight' ? 'queued' : s === 'full' ? 'waiting' : 'idle';
}

/** "Has room", "Nearly full", "Full" for a machine taking work; "Offline", "Disabled" (drained), "Outside hours" otherwise. */
export function nodeStatus(c: NodeCapacity): { label: string; tone: StatusTone } {
  if (!c.online) return { label: 'Offline', tone: 'offline' };
  if (c.drained) return { label: 'Disabled', tone: 'idle' };
  if (c.blockedBy) return { label: 'Outside hours', tone: 'idle' };
  if (c.limit === 0) return { label: 'No new work', tone: 'idle' };
  if (c.admission) return { label: ADMISSION_STATE_LABEL[c.admission.state], tone: admissionTone(c.admission.state) };
  if (!c.accepting) return { label: 'Busy', tone: 'waiting' };
  if (c.limit === 0) return { label: 'No new work', tone: 'idle' };
  if (c.used >= c.limit) return { label: 'Full', tone: 'waiting' };
  return { label: 'Accepting work', tone: 'working' };
}

/** The words after a machine's status: why it takes no new work, or who drained it. */
/**
 * Bytes a machine's agent sends the hub per minute, averaged since it connected (ADR 0104), from
 * the counters it reports; null until it has reported and been connected a full minute.
 */
export function wirePerMinute(m: Pick<Machine, 'wire' | 'online'>, now: number): number | null {
  const w = m.wire;
  if (!w || !m.online || w.since <= 0) return null;
  // The last whole minute is the steady rate; the average since connect carries the connect burst.
  if (w.lastMinute) return w.lastMinute.bytes;
  const elapsed = now - w.since;
  if (elapsed < 60_000) return null;
  return (w.bytes / elapsed) * 60_000;
}

/** The fleet's bytes per minute to the hub, over the machines that report it; null when none does. */
export function fleetWirePerMinute(machines: readonly Pick<Machine, 'wire' | 'online'>[], now: number): number | null {
  let sum = 0;
  let any = false;
  for (const m of machines) {
    const r = wirePerMinute(m, now);
    if (r === null) continue;
    sum += r;
    any = true;
  }
  return any ? sum : null;
}

export function nodeDetail(c: NodeCapacity, now: number): string | null {
  if (!c.online) return c.drained ? 'and drained' : null;
  if (c.drained) {
    const mins = Math.max(1, Math.round((now - c.drained.at) / 60_000));
    const ago = mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`;
    return `by ${c.drained.by} ${ago} ago${c.drained.reason ? `: ${c.drained.reason}` : ''}`;
  }
  if (c.blockedBy) return `until ${c.blockedBy.to === '24:00' ? 'midnight' : c.blockedBy.to} (${c.blockedBy.label} rule)`;
  if (c.admission && c.limit > 0) return admissionDetail(c.admission);
  if (!c.accepting && c.notAccepting) return c.notAccepting.replace(`${c.name} is `, '').replace(`${c.name} `, '');
  return null;
}

/**
 * The words after a machine's admission state: how many more sessions fit when it has room, else
 * why it is nearly full or full ("12 of 18 slots in use, 2 waiting", "memory 92% used").
 */
export function admissionDetail(a: Admission): string {
  if (a.state === 'room') return `${a.more} more ${a.more === 1 ? 'session fits' : 'sessions fit'}`;
  return a.text.replace(/^Memory/, 'memory');
}

/** "UTC+04:00", or "Hub time zone" for null. */
export function offsetLabel(min: number | null): string {
  if (min == null) return 'Hub time zone';
  const sign = min < 0 ? '-' : '+';
  const a = Math.abs(min);
  return `UTC${sign}${String(Math.floor(a / 60)).padStart(2, '0')}:${String(a % 60).padStart(2, '0')}`;
}

export const ruleText = describeRule;

/** Counts for the header strip. */
export function capacityTotals(cap: readonly NodeCapacity[], queue: readonly QueueEntry[]) {
  const online = cap.filter((c) => c.online);
  return {
    slots: online.filter((c) => c.accepting).reduce((s, c) => s + c.limit, 0),
    used: online.reduce((s, c) => s + c.used, 0),
    free: online.filter((c) => c.accepting).reduce((s, c) => s + Math.max(0, c.limit - c.used), 0),
    queued: queue.filter((e) => e.state === 'queued' || e.state === 'starting').length,
    drained: cap.filter((c) => !!c.drained).length,
    machines: cap.length,
    online: online.length,
    /** Sessions that fit now across machines (ADR 0063 step 5: heavy work and memory, else session count). */
    room: online.reduce((s, c) => s + (c.admission ? c.admission.more : c.accepting ? Math.max(0, c.limit - c.used) : 0), 0),
    /** The work budgets of machines that report them: slots in use, the budget, heavy commands waiting. */
    work: online.reduce(
      (s, c) => (c.admission?.work ? { used: s.used + c.admission.work.used, budget: s.budget + c.admission.work.budget, waiting: s.waiting + c.admission.work.waiting, machines: s.machines + 1 } : s),
      { used: 0, budget: 0, waiting: 0, machines: 0 },
    ),
  };
}

/** Day toggles in a rule form, Monday first. */
export const WEEK: readonly { day: number; short: string }[] = [
  { day: 1, short: 'Mon' },
  { day: 2, short: 'Tue' },
  { day: 3, short: 'Wed' },
  { day: 4, short: 'Thu' },
  { day: 5, short: 'Fri' },
  { day: 6, short: 'Sat' },
  { day: 0, short: 'Sun' },
];
